import { useCallback, useEffect, useState } from "react";
import api from "@/lib/api";
import { formatCurrency, formatDate } from "@/components/pcp";
import { ArrowsLeftRight, Plus } from "@phosphor-icons/react";
import { ConciliacaoTabela } from "./ConciliacaoTabela";
import { ReprogramacaoForm } from "./ReprogramacaoForm";
import { ReprogramacaoDetalhe } from "./ReprogramacaoDetalhe";
import { StatusChip, TIPO_LABEL } from "./reprogUtil";

function Kpi({ label, value, cor, testid, sub }) {
  return (
    <div style={{ padding: "10px 12px", border: "1px solid #E7E5E4", borderRadius: 6, background: "#fff" }} data-testid={testid}>
      <div className="kpi-label">{label}</div>
      <div className="font-mono font-semibold" style={{ fontSize: "1.05rem", color: cor }}>{value}</div>
      {sub && <div style={{ fontSize: "0.65rem", color: "#71717A" }}>{sub}</div>}
    </div>
  );
}

export function ReprogramacaoContrato({ contrato, onReload }) {
  const [conc, setConc] = useState(null);
  const [lista, setLista] = useState([]);
  const [form, setForm] = useState(null);      // null | { existente }
  const [detalhe, setDetalhe] = useState(null); // rid
  const [aberto, setAberto] = useState(false);
  const load = useCallback(async () => {
    const [c, l] = await Promise.all([api.get(`/contratos/${contrato.id}/conciliacao`), api.get(`/contratos/${contrato.id}/reprogramacoes`)]);
    setConc(c.data); setLista(l.data);
  }, [contrato.id]);
  useEffect(() => { load(); }, [load]);
  if (!conc) return null;
  const r = conc.resumo;
  const recarregar = () => { load(); onReload(); };
  const precisaAtencao = r.valor_pendente_cobertura > 0 || r.propostas_pendentes > 0;

  // Recolhido por padrão: só uma linha. Mostra alerta quando há excedente sem cobertura ou proposta em tramitação.
  if (!aberto) {
    return (
      <div className="pcp-card" style={{ padding: "10px 16px", marginBottom: 20, display: "flex", justifyContent: "space-between", alignItems: "center", gap: 10, flexWrap: "wrap", borderLeft: precisaAtencao ? "4px solid #DC2626" : undefined }} data-testid="reprogramacao-contrato">
        <div style={{ display: "flex", alignItems: "center", gap: 10, fontSize: "0.85rem" }}>
          <ArrowsLeftRight size={16} weight="bold" />
          <span className="font-display font-bold">Reprogramação contratual</span>
          {precisaAtencao ? (
            <span style={{ fontSize: "0.75rem", color: "#991B1B" }} data-testid="reprog-alerta">
              {r.valor_pendente_cobertura > 0 && <>{formatCurrency(r.valor_pendente_cobertura)} executados sem cobertura (não faturáveis)</>}
              {r.valor_pendente_cobertura > 0 && r.propostas_pendentes > 0 && " · "}
              {r.propostas_pendentes > 0 && <>{r.propostas_pendentes} proposta(s) em tramitação</>}
            </span>
          ) : <span style={{ fontSize: "0.75rem", color: "#71717A" }}>Sem excedente pendente · saldo global {formatCurrency(r.saldo_financeiro_global)}</span>}
        </div>
        <button type="button" className="btn btn-ghost btn-sm" onClick={() => setAberto(true)} data-testid="reprog-toggle">Abrir</button>
      </div>
    );
  }

  return (
    <div className="pcp-card" style={{ padding: 20, marginBottom: 20 }} data-testid="reprogramacao-contrato">
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", gap: 10, flexWrap: "wrap", marginBottom: 12 }}>
        <div>
          <h3 className="font-display font-bold" style={{ fontSize: "1.05rem" }}><ArrowsLeftRight size={16} weight="bold" style={{ verticalAlign: -2 }} /> Reprogramação contratual e conciliação</h3>
          <div style={{ fontSize: "0.75rem", color: "#71717A" }}>Execução física × cobertura contratual × faturado. Excedente sem cobertura não entra no boletim até a formalização do ajuste.</div>
        </div>
        <div style={{ display: "flex", gap: 8 }}>
          <button type="button" className="btn btn-ghost btn-sm" onClick={() => setAberto(false)} data-testid="reprog-toggle">Recolher</button>
          <button type="button" className="btn btn-outline btn-sm" onClick={() => setForm({ existente: null })} data-testid="btn-nova-reprogramacao"><Plus size={14} weight="bold" /> Nova proposta</button>
        </div>
      </div>

      <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(150px, 1fr))", gap: 8, marginBottom: 14 }} data-testid="reprog-kpis">
        <Kpi label="Cobertura contratual" value={formatCurrency(r.valor_coberto_total)} sub="contrato + aditivos + reprogramações aplicadas" testid="kpi-coberto" />
        <Kpi label="Faturado (medições)" value={formatCurrency(r.valor_faturado)} testid="kpi-faturado" />
        <Kpi label="Saldo financeiro global" value={formatCurrency(r.saldo_financeiro_global)} cor={r.saldo_financeiro_global < 0 ? "#DC2626" : "#059669"} testid="kpi-saldo-global" />
        <Kpi label="Pendente de cobertura" value={formatCurrency(r.valor_pendente_cobertura)} cor={r.valor_pendente_cobertura > 0 ? "#DC2626" : undefined} sub="executado acima da cobertura, não faturado" testid="kpi-pendente" />
        <Kpi label="Disponível p/ reprogramar" value={formatCurrency(r.valor_disponivel_reprogramacao)} sub="não executado − comprometido" testid="kpi-disponivel" />
        <Kpi label="Propostas pendentes" value={`${r.propostas_pendentes} · ${formatCurrency(r.valor_propostas_pendentes)}`} testid="kpi-propostas" />
        <Kpi label="Formalizado e aplicado" value={`${r.reprogramacoes_aplicadas} · ${formatCurrency(r.valor_formalizado)}`} testid="kpi-formalizado" />
      </div>

      {(
        <>
          <ConciliacaoTabela conc={conc} />
          <div style={{ marginTop: 16 }}>
            <div className="overline" style={{ marginBottom: 6 }}>Propostas de reprogramação ({lista.length})</div>
            {lista.length === 0 ? <div style={{ color: "#71717A", fontSize: "0.85rem" }}>Nenhuma proposta. Use "Nova proposta" para remanejar saldo não executado para um item com excedente.</div> : (
              <table className="pcp-table" data-testid="reprog-lista">
                <thead><tr><th>Nº</th><th>Título / tipo</th><th>Status</th><th style={{ textAlign: "right" }}>Origens</th><th style={{ textAlign: "right" }}>Destinos</th><th style={{ textAlign: "right" }}>Impacto</th><th>Instrumento</th><th>Atualizado</th></tr></thead>
                <tbody>
                  {lista.map((p) => (
                    <tr key={p.id} style={{ cursor: "pointer" }} onClick={() => setDetalhe(p.id)} data-testid={`reprog-row-${p.numero}`}>
                      <td className="font-mono font-semibold">{p.numero}</td>
                      <td>{p.titulo || "—"}<div style={{ fontSize: "0.65rem", color: "#71717A" }}>{TIPO_LABEL[p.tipo]}</div></td>
                      <td><StatusChip status={p.status} /></td>
                      <td className="font-mono" style={{ textAlign: "right" }}>{formatCurrency(p.valor_origens)}</td>
                      <td className="font-mono" style={{ textAlign: "right" }}>{formatCurrency(p.valor_destinos)}</td>
                      <td className="font-mono" style={{ textAlign: "right" }}>{formatCurrency(p.impacto_global)}</td>
                      <td style={{ fontSize: "0.75rem" }}>{p.formalizacao ? `${p.formalizacao.instrumento} ${p.formalizacao.numero}` : p.manifestacao_fiscal ? <span style={{ color: "#B45309" }}>só manifestação do fiscal</span> : "—"}</td>
                      <td style={{ fontSize: "0.72rem" }}>{formatDate((p.historico || []).slice(-1)[0]?.data)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>
          {(contrato.planilha_versoes || []).length > 0 && (
            <div style={{ marginTop: 12, fontSize: "0.72rem", color: "#57534E" }} data-testid="reprog-versoes">
              <b>Versões da planilha:</b> {contrato.planilha_versoes.map((v) => `v${v.versao} (${formatDate(v.vigencia_inicio)}) ${formatCurrency(v.valor_total)} — ${v.motivo}`).join(" · ")}
            </div>
          )}
        </>
      )}

      {form && <ReprogramacaoForm contrato={contrato} conc={conc} existente={form.existente} onClose={() => setForm(null)} onSaved={(rp) => { setForm(null); recarregar(); setDetalhe(rp.id); }} />}
      {detalhe && <ReprogramacaoDetalhe contrato={contrato} rid={detalhe} onClose={() => setDetalhe(null)} onChanged={recarregar} onEditar={(rp) => { setDetalhe(null); setForm({ existente: rp }); }} />}
    </div>
  );
}
